/**
 * English names and texts for game content, keyed by content id. The
 * packages keep Vietnamese as the source (the Worker never needs English);
 * anything missing here simply shows the Vietnamese original.
 */

export const HOUSES_EN: Record<string, { readonly name: string; readonly description: string }> = {
  sorting: {
    name: "Sorting House",
    description: "The shopkeeper made a huge mess! Help sort the gifts, toys and sweets into the right trays.",
  },
  logic: { name: "Logic Tower", description: "Drop marbles past flipping levers and guess where each one rolls." },
  tiles: {
    name: "Tile Castle",
    description: "Stroll the jade halls and pick tiles into your satchel, three alike make a shiny charm.",
  },
  arcade: { name: "Fun Arcade", description: "A cheeky claw machine at the fair, plan every coin to win the plushies." },
  colors: { name: "Color Lab", description: "Mix sparkly colors and pour them into the right vials, careful not to smudge." },
  fill: { name: "Magic Workshop", description: "Drop rolls of magic silk to wrap the gift box snugly, using as few rolls as you can." },
};

export const ARCHETYPES_EN: Record<string, string> = {
  wizard: "Wizard",
  sorceress: "Sorceress",
  princess: "Princess",
  prince: "Prince",
  "angel-boy": "Angel Boy",
  "angel-girl": "Angel Girl",
  "demon-boy": "Imp Boy",
  "demon-girl": "Imp Girl",
};

export const FAMILIES_EN: Record<string, string> = {
  mage: "Mages",
  fairytale: "Fairy Tale",
  divine: "Wings and Horns",
};

export const ITEMS_EN: Record<string, string> = {
  "hat-wizard": "Wizard hat",
  "tiara-pink": "Pink tiara",
  "crown-gold": "Gold crown",
  "hat-sorceress": "Pink sorceress hat",
  "halo-gold": "Golden halo",
  "horns-imp": "Cheeky horn headband",
  "crown-roses": "Rose crown",
  "crown-star": "Star crown",
  "bunny-ears": "Bunny ears",
  "hat-witch": "Purple witch hat",
  "hat-party": "Party hat",
  "bow-pink": "Pink bow",
  "clip-star": "Star clip",
  "clip-daisy": "Daisy clip",
  "clip-butterfly": "Butterfly clip",
  "glasses-round": "Round glasses",
  "glasses-heart": "Heart glasses",
  "glasses-star": "Star glasses",
  "mask-masquerade": "Masquerade mask",
  "necklace-pearl": "Pearl necklace",
  "necklace-heart": "Heart necklace",
  "bowtie-red": "Red bow tie",
  "scarf-striped": "Striped scarf",
  "medal-star": "Star medal",
  "lei-flower": "Flower lei",
  "scarf-rainbow": "Rainbow scarf",
  "collar-bell": "Bell collar",
  "cape-red": "Red cape",
  "wings-fairy": "Fairy wings",
  "wings-angel": "Angel wings",
  "cape-star": "Star cape",
  "wand-heart": "Heart wand",
  "staff-crystal": "Crystal staff",
  "lollipop-giant": "Giant lollipop",
  "balloon-star": "Star balloon",
  "aura-sparkle": "Sparkle aura",
  "aura-hearts": "Heart aura",
  "aura-rainbow": "Rainbow aura",
  "aura-snow": "Snowflake aura",
};

export const SHOP_TABS_EN: Record<string, string> = {
  all: "All",
  head: "Hats and crowns",
  hair_accessory: "Hair",
  face: "Glasses",
  neck: "Necklaces",
  back: "Wings and capes",
  hand: "Props",
  aura: "Effects",
};

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Per achievement family: its name and how a tier's target reads. */
export const ACHIEVEMENTS_EN: Record<string, { readonly name: string; readonly describe: (target: number) => string }> = {
  "first-steps": { name: "First Steps", describe: () => "Finish your first level" },
  levels: { name: "Super Sorter", describe: (n) => `Finish ${count(n, "level")}` },
  stars: { name: "Star Picker", describe: (n) => `Collect ${count(n, "star")}` },
  perfect: { name: "Perfect", describe: (n) => `Get 3 stars on ${count(n, "level")}` },
  chapters: { name: "A New Page", describe: (n) => `Finish ${count(n, "chapter")}` },
  "perfect-chapters": { name: "Laurel Wreath", describe: (n) => `Get all 30 stars in ${count(n, "chapter")}` },
  shopper: { name: "Shopping Star", describe: (n) => `Own ${count(n, "item")} bought in the shop` },
  stylist: { name: "Fashion Sense", describe: (n) => `Wear items in ${count(n, "spot")} at once` },
  treasures: { name: "Rare Treasures", describe: (n) => `Own ${count(n, "Epic item")}` },
  "safe-journey": { name: "Safe Journey", describe: () => "Make a recovery code for your journey" },
  "quick-hands": { name: "Quick Hands", describe: (n) => `Finish ${count(n, "level")} in under 30 seconds` },
  daily: { name: "Fun Every Day", describe: (n) => `Play on ${count(n, "different day")}` },
};

/** Chapter names and what each chapter is about, per house, in chapter order. */
export const CHAPTERS_EN: Record<string, readonly { readonly name: string; readonly focus: string }[]> = {
  sorting: [
    { name: "Toy Shop", focus: "Sort by kind of thing" },
    { name: "Sweet Counter", focus: "More treats, more trays, sort by size" },
    { name: "Garden", focus: "Shapes and kinds" },
    { name: "Bow Workshop", focus: "Mix colors and shapes" },
    { name: "Secret Storeroom", focus: "Trays with limits, items arrive in turn" },
    { name: "Magic Festival", focus: "Magic bells change the tray rules" },
    { name: "Night Shop", focus: "More groups" },
    { name: "Gift Room", focus: "Stack from big to small" },
    { name: "Fun Fair", focus: "Many rules together" },
    { name: "Grand Celebration", focus: "Master challenge" },
  ],
  colors: [
    { name: "First Lessons", focus: "Pour colors and cork each vial" },
    { name: "Tight Shelf", focus: "Just one empty vial, plan a few steps ahead" },
    { name: "Mist", focus: "Mist hides the lower layers until you pour down to them" },
    { name: "Tall and Short", focus: "Every color needs a vial that fits just right" },
    { name: "Seals", focus: "A sealed vial opens once the color on its seal is done" },
    { name: "Shades", focus: "Lookalike colors, check the marks closely" },
    { name: "Mixing Flask", focus: "Mix two base colors into a new one" },
    { name: "Mist and Seals", focus: "Mist meets the golden seal" },
    { name: "Odd Vial Mixing", focus: "Tall and short vials with the mixing flask" },
    { name: "Masterpiece", focus: "Every trick in one level" },
  ],
  tiles: [
    { name: "Castle Gate", focus: "Walk, pick up tiles, match three" },
    { name: "Paved Hall", focus: "Stone slabs can be crossed again and again" },
    { name: "Stacked Tiles", focus: "Two-layer cells, visit them twice" },
    { name: "Small Satchel", focus: "The satchel has fewer pockets" },
    { name: "Jade Locks", focus: "A door opens once its charm is made" },
    { name: "Magic Mirrors", focus: "Step into one mirror, come out of the other" },
    { name: "Face-Down Tiles", focus: "Face-down tiles show their carving when you stand beside them" },
    { name: "Locks and Face-Down Tiles", focus: "Jade locks meet face-down tiles" },
    { name: "Mirrors and Stacks", focus: "Magic mirrors meet stacked tiles" },
    { name: "Masterpiece", focus: "Every trick, finish on the golden throne" },
  ],
  logic: [
    { name: "Rolling Paths", focus: "Drop into the right column, meet the flipping lever" },
    { name: "Flip Levers", focus: "More levers, send marbles round on purpose" },
    { name: "Three Colors", focus: "Wider towers, three colors at once" },
    { name: "Gears", focus: "Two levers on one gear flip together" },
    { name: "Brass Lids", focus: "A lidded cup waits for another color to fill" },
    { name: "Color Levers", focus: "A lever flips only under its own color" },
    { name: "Two Hoppers", focus: "Pick the hopper as well as the column" },
    { name: "Gears and Lids", focus: "Gears meet brass lids" },
    { name: "Color Levers, Two Hoppers", focus: "Color levers meet two hoppers" },
    { name: "Masterpiece Tower", focus: "Every trick in one tower" },
  ],
  arcade: [
    { name: "Slippery Claw", focus: "Carry prizes home in hops, a landing knocks one flying" },
    { name: "Strong Turns", focus: "Weak and strong grips take turns" },
    { name: "Tall Piles", focus: "A full pile makes a knocked prize roll on" },
    { name: "Heavyweights", focus: "Big plushies travel one column less" },
    { name: "Ribbon Pairs", focus: "Two friends tied with a bow are lifted together" },
    { name: "Glass Walls", focus: "The claw slips right before a glass wall" },
    { name: "Bouncy Balls", focus: "A ball bounces one more column when it drops" },
    { name: "Heavy and Tied", focus: "Heavyweights meet ribbon pairs" },
    { name: "Glass and Bounce", focus: "Glass walls meet bouncy balls" },
    { name: "Grand Machine", focus: "Every trick in the biggest machine at the fair" },
  ],
  fill: [
    { name: "First Silk", focus: "Rolls go right and down and stop at the wall of the box" },
    { name: "Bows", focus: "A bow tied in the box stops the silk" },
    { name: "Four Ways", focus: "Rolls that go left and up join the shelf" },
    { name: "Silk First", focus: "Lay the border first so the columns stop where you want" },
    { name: "Handkerchiefs", focus: "Short rolls only cover a few slots" },
    { name: "Magic Scissors", focus: "Silk that reaches the scissors is cut right there" },
    { name: "Embroidery", focus: "An embroidered slot only takes silk along its thread" },
    { name: "Scissors and Thread", focus: "Magic scissors and embroidered slots in one box" },
    { name: "Tailor Tricks", focus: "Handkerchiefs, scissors and thread in one box" },
    { name: "Masterpiece Box", focus: "The biggest boxes, plus double rolls that go both ways" },
  ],
};

/** Tutorial steps per house and tutorial id, in step order. */
export const TUTORIALS_EN: Record<string, Record<string, readonly string[]>> = {
  sorting: {
    "drag-to-bin": ["Drag the item into the tray with the matching picture!"],
    "read-labels": ["The picture on each tray tells you exactly what it is craving."],
    undo: ["Put something in the wrong place? Tap Undo to turn back time!"],
    "size-sort": ["This time the trays only care about big or small!"],
    "shape-sort": ["These trays only care about shape, star, heart or round."],
    "two-rules": ["These trays are picky, the color and the shape both have to match!"],
    "capacity-queue": [
      "Items arrive in a line, you can only take the one at the front.",
      "Each tray only holds so many items, plan it out!",
    ],
    "magic-bell": [
      "Put the magic bell on the pedestal and the tray rules change right away!",
      "Finish the old rule first, then ring the bell!",
    ],
    "many-groups": ["More trays to play with! You can also drag items from one tray to another."],
    "stack-order": ["Glass jars stack up, the big item goes at the bottom and smaller ones on top."],
    "move-limit": ["This level has a move limit. Run out and just restart, you lose nothing!"],
    hint: [
      "Stumped? The hint bulb shows the way. Every level has 2 free hints, then they cost gold, and using a hint means that level gets just 1 star.",
    ],
  },
  colors: {
    "lab-pour": [
      "Welcome to the Color Lab! The essence vials got all mixed up, let's sort them out.",
      "Tap the glowing vial, then tap the vial it points to, and pour. A color only pours into an empty vial or onto the same color.",
    ],
    "lab-cork": ["Gather every part of one color into one vial and it gets a cork, one color done!"],
    "lab-undo": [
      "Poured the wrong way? Undo as much as you like, it costs nothing.",
      "The target number is up top. Finish within the target to grab all three stars.",
    ],
    "lab-hint": ["Stuck? Tap the bulb. Every level has 2 free hints, after that you can trade gold for more."],
    "lab-tight": ["The shelf is tight! Only one empty vial left, pour at random and you get stuck. Plan a few steps ahead."],
    "lab-mist": [
      "Mist hides the bottom layers. Pour off the top layers and the colors underneath peek out.",
      "Not sure what is hiding? Keep a way out and don't rush to fill the empty vial.",
    ],
    "lab-sizes": ["Tall vials and short vials! A color only gets corked in a vial that fits exactly as many parts as it has."],
    "lab-seal": ["A vial with a golden seal is locked. Cork the color on the seal and it opens right up."],
    "lab-shades": ["Colors so alike they make your eyes spin! The mark on each layer tells them apart."],
    "lab-flask": [
      "This is the magic mixing flask. Pour in one part of a base color, add a part of another and you get a new color.",
      "The recipes are right up top, just glance up if you forget, nobody is grading your memory.",
      "After mixing, pour the new color into a vial. The flask must be empty when the level ends.",
    ],
    "lab-mist-seal": ["Mist and seals together! Plan for whichever color shows up first."],
    "lab-odd-flask": ["Tall and short vials meet the mixing flask! Mix just enough to fit that color's vial."],
    "lab-masterpiece": ["The masterpiece chapter! Every trick shows up, take your time, nobody is rushing you."],
  },
  tiles: {
    "jade-walk": [
      "Welcome to the Jade Castle! A wizard scattered tiles all over the hall, let's pick them up for the castle.",
      "Tap the cell next to you to step there. Step onto a tile and it flies straight into your satchel.",
    ],
    "jade-satchel": [
      "Three tiles with the same carving meet in the satchel and turn into a jade charm, freeing up room.",
      "When the satchel is full and the new tile cannot make a three, there is no room for it and that step is a no.",
    ],
    "jade-socket": [
      "A cell you have emptied becomes a hole, you cannot cross it again. Plan your route so no tile gets left behind.",
      "Took a wrong step? Tap Undo, it is free and you can use it as often as you like.",
    ],
    "jade-gold": [
      "See the gold pin on the satchel? Keep the satchel from passing the gold line to grab all three stars.",
      "Stumped? Tap the bulb for one step. Every level has 2 free hints, after that you can trade gold for more.",
    ],
    "jade-stone": ["Stone slabs are never picked up, walk over them as much as you like. Tap a faraway tile and you walk there over the stones."],
    "jade-double": [
      "Stacked tiles! The first visit picks up the top tile, the lower one shows its carving on the side, come back again to take it.",
    ],
    "jade-small-bag": ["The satchel shrank to fewer pockets. Make your threes early before it gets crowded."],
    "jade-lock": [
      "This jade door is locked tight. The carving on the door is its key.",
      "Make one charm with that carving and the door opens by itself, becoming a slab you can cross freely.",
    ],
    "jade-mirror": ["Magic mirrors! Step into this mirror and you pop out of the other one, as many times as you like."],
    "jade-face-down": ["Some tiles lie face down and only turn over when you stand beside them. Not sure what they hide? Keep a way back."],
    "jade-lock-face-down": ["Locks meet face-down tiles! Turn the tiles over first, then work out where the key is."],
    "jade-mirror-double": ["Mirrors meet stacked tiles, and the satchel got smaller. Visit a stack twice? Keep a way back through the mirror."],
    "jade-throne": [
      "The masterpiece chapter! The golden throne only opens once every tile in the hall is picked up.",
      "After the last tile you still have to walk to the throne, so keep a way up to it.",
    ],
  },
  logic: {
    "tower-drop": [
      "Welcome to the Logic Tower! The clockmaker needs colored marbles to wind up the tower, let's lend a hand.",
      "Marbles wait on the rail. Slide the marble left and right to see the path it will roll, let go and it drops.",
      "A marble that lands in a cup of its color stays there. Fill every cup and the tower rings its bells.",
      "Try dropping the first one into the column with the sparkle.",
    ],
    "tower-lever": [
      "This is a flip lever. A marble rolls the way the lever leans, and once it has passed the lever flips to the other side.",
      "The next marble over that same lever turns the other way. Peek at the levers before you drop, that's the secret.",
    ],
    "tower-return": [
      "A marble that falls into an empty gutter, a cup of another color or a full cup rides the lift back to the end of the line.",
      "No marble is ever lost, it only costs a drop. Sending one round on purpose to flip a lever back is a pro move.",
      "Undo as much as you like, it's free. Three stars count the drops you keep in the end.",
    ],
    "tower-hint": [
      "The target number is up top. Finish within it and the three stars are yours.",
      "Stuck? Tap the light bulb. Each level has 2 free hints, and a hint shows just one next drop.",
    ],
    "tower-levers": [
      "Welcome to Flip Levers! More levers and more marbles. Before you drop, check which levers this marble flips and where the next one will go.",
      "Sending one marble round the lift at just the right moment to set a lever back is often the neatest line.",
    ],
    "tower-colors": ["Now there are three marble colors. Every marble has its own mark, so no mixing them up."],
    "tower-gear": ["Two levers on one gear are twins. When one flips the other flips too, even if no marble touches it."],
    "tower-lid": ["A cup with a brass lid stays shut until the cups of the gem's color on the lid are full. Think about which color goes first."],
    "tower-keyed": ["A lever with a colored knob is picky. Only a marble of that color flips it, others roll by and it stays put."],
    "tower-hoppers": ["This level has two hoppers. Tap a hopper to put its front marble on the rail, then pick a column as usual."],
    "tower-gear-lid": [
      "Gears meet brass lids! Flip one lever and its twin flips too, and a lidded cup still waits for another color. Work out the order of colors before you drop.",
    ],
    "tower-keyed-hoppers": ["Color levers meet two hoppers! Picking which hopper to draw from is a move in itself, since only the right color flips a color lever."],
    "tower-finale": ["The final tower has gears, brass lids and color levers. Take your time, nobody is rushing you."],
  },
  arcade: {
    "claw-intro": [
      "Welcome to the Fun Arcade! This claw machine is a cheeky one, so plan well to win your prizes.",
      "The wish card lists the friends a child is hoping for. Bring every friend with a gold ticket down into the prize chute on the left.",
      "The lamps on top show the claw's grip. It is weak and slips after the number of columns on the lamp, two in this machine.",
      "Tap a column to spend a coin. Slide across the columns to see where the prize will land first. Try the sparkling column.",
    ],
    "claw-knock": [
      "A prize that slips onto another one knocks it one column to the left. Knock it into the chute and it is yours, no coin needed!",
    ],
    "claw-target": [
      "The number by the bullseye is the most coins for three stars. The neatest solution needs one coin less.",
      "Undo as much as you like, it costs nothing. Stars only count the coins you kept when every wish is won.",
    ],
    "claw-hint": ["Stuck? Tap the bulb and the machine shows one good column. Using a hint caps that level at 2 stars."],
    "claw-grips": [
      "From now on the grip follows the rhythm on the lamps. Each dot on a lamp is one column the claw carries, a lamp with more dots is a strong turn.",
      "To wait for a strong turn, spend a coin on an empty column. The claw grabs air and only moves the rhythm on, sometimes that is the clever move.",
    ],
    "claw-tall": ["This machine is low, so each pile holds only a few prizes. A knocked prize that meets a full pile rolls on to the left, sometimes straight into the chute."],
    "claw-heavy": [
      "A friend wearing a weight is a heavyweight. The claw carries it one column less, and a weak turn only lifts it and drops it back.",
      "Nothing that lands on a heavyweight can knock it away, the prize just sits on top.",
    ],
    "claw-ribbon": ["Two friends tied with one bow always move together. Lift the top one and the one below comes too, and a knock sends both flying."],
    "claw-glass": [
      "A hanging glass wall blocks the claw. Carrying a prize up to the wall, it bumps it and lets go right in front.",
      "But knocked prizes fly under the wall. To get a friend across, knock it over.",
    ],
    "claw-bouncy": ["The striped beach ball bounces. Dropped, it hops one more column, knocked, it flies two."],
    "claw-heavy-ribbon": ["Heavyweights meet ribbons! A pair with a heavyweight in it is heavy, so the claw carries it one column less and nothing knocks it."],
    "claw-glass-bouncy": ["Glass walls meet bouncy balls! A ball dropped in front of a wall can still bounce under it."],
    "claw-finale": ["The grand machine has every trick. Take your time and look before each coin, nobody is rushing you."],
  },
  fill: {
    "silk-basics": [
      "Welcome to the Magic Workshop! A customer ordered a gift box, let's wrap it with magic silk.",
      "The shelf holds rolls of magic silk. Each roll only rolls the way its arrow points. Tap a roll to pick it up.",
      "Drop the roll on an empty slot and the silk unrolls straight to the wall of the box. Try the slot with the sparkle!",
    ],
    "silk-stop": [
      "Silk that meets silk already laid stops right there. Lay a row first and columns dropped later stop right on top of it.",
      "Cover every slot and the gift is wrapped, the customer will be thrilled!",
    ],
    "silk-stars": [
      "The number by the bullseye is the three star target. Fewer rolls means more stars, spare rolls can stay on the shelf.",
      "Undo as much as you like, it costs nothing. Stars only count the rolls still in the box when it is wrapped.",
    ],
    "silk-hint": ["Stuck? Tap the bulb. Each level has 2 free hints, and a hint shows just one next roll."],
    "silk-bows": [
      "Welcome to Bows! A bow already tied needs no silk, and silk that meets a bow stops too.",
      "Think of bows as little walls. A slot stuck between two bows often needs a roll of its own.",
    ],
    "silk-four": ["The shelf now has rolls that go left and up. A narrow pocket next to a bow is easier to fill with silk running towards that bow."],
    "silk-first": [
      "The secret of this chapter: lay the border first, and the columns you drop later stop right on it.",
      "The middle row looks as long as the border, but laying it first cuts those columns in two. Think it through!",
    ],
    "silk-hanky": ["Handkerchiefs are short rolls, and the dots on a roll are the most slots it covers. Spot a gap that fits just right and drop it there."],
    "silk-scissors": [
      "Magic scissors! Silk that reaches a slot with scissors covers that slot and is cut right there.",
      "A roll dropped right on the scissors covers just that one slot. Use the scissors to stop silk halfway.",
    ],
    "silk-thread": [
      "An embroidered slot only takes silk running along its thread. Thread across takes silk across, thread down takes silk down.",
      "Silk running against the thread stops right before that slot, as if it met a wall.",
    ],
    "silk-combo": ["Scissors and Thread! Scissors cut the silk, embroidered slots stop silk running against their thread. Find where the silk stops before you drop."],
    "silk-tricks": ["Tailor Tricks: handkerchiefs, magic scissors and embroidered slots in one box. Look at each slot before you drop."],
    "silk-masterpiece": [
      "The masterpiece box! The biggest box, with every trick of the workshop.",
      "The shelf now has double rolls that unroll both ways wherever you drop them. Use one in place of a left or a right roll.",
    ],
  },
};

export const LAB_COLORS_EN: Record<string, string> = {
  ruby: "Ruby",
  honey: "Honey",
  sky: "Sky blue",
  jade: "Jade",
  amethyst: "Amethyst",
  coral: "Coral",
  pearl: "Pearl",
  teal: "Teal",
  rose: "Rose pink",
  sapphire: "Sapphire",
  lime: "Lime",
  lilac: "Lilac",
  mint: "Mint",
  amber: "Amber",
  plum: "Plum",
  peach: "Peach",
};

/** Sorting House tray labels, keyed by tag. */
export const TAGS_EN: Record<string, string> = {
  "type:toy": "Toys",
  "type:plush": "Plushies",
  "type:candy": "Candy",
  "type:cake": "Cakes",
  "type:cookie": "Cookies",
  "type:fruit": "Fruit",
  "type:garden": "Garden",
  "type:night": "Night things",
  "type:magic": "Magic",
  "type:gem": "Gems",
  "type:balloon": "Balloons",
  "type:craft": "Sewing",
  "type:gift": "Gift boxes",
  "type:doll": "Dolls",
  "shape:star": "Star",
  "shape:heart": "Heart",
  "shape:round": "Round",
  "shape:drop": "Drop",
  "color:pink": "Pink",
  "color:blue": "Blue",
  "color:yellow": "Yellow",
  "color:green": "Green",
  "color:purple": "Purple",
  "color:orange": "Orange",
  "size:s": "Small",
  "size:m": "Medium",
  "size:l": "Large",
  "size:xl": "Extra large",
  "special:switch": "Magic bell",
};
